import type { AdConsent } from '~/types/AdConsent';
import apiRoutes from '~/const/apiRoutes';
import {
   clearPixelUser,
   identifyPixelUser,
   pausePixel,
   setPixelAllowed,
   trackEvent,
} from '~/composables/useMetaPixel';

// The signed-in side of the pixel: it asks the API whether this client may be
// tracked, tells the pixel, and fires the conversions the API's ledger offers.

export type PixelPending = {
   user?: { em?: string; external_id: string };
   events: { name: string; event_id: string; value?: number; currency?: string }[];
};

export type PixelAccountApi = {
   fetchConsent: () => Promise<{ consent: AdConsent | null; error: unknown }>;
   fetchPending: () => Promise<PixelPending | null>;
   markFired: (eventIds: string[]) => Promise<void>;
};

export type PixelAccountContext = {
   // Null when nobody is signed in.
   accountKey: string | null;
   accountId: string | null;
   email?: string;
   impersonating: boolean;
};

export type PixelAccountPixel = {
   setAllowed: (allowed: boolean) => void;
   pause: (dropHeld?: boolean) => void;
   identify: (email: unknown, accountId: unknown, fallbackEmailHash?: string) => Promise<void>;
   clear: () => void;
   trackEvent: (
      name: string,
      params: Record<string, unknown>,
      eventID?: string,
      onSent?: () => void,
   ) => boolean;
};

const PENDING_INTERVAL_MS = 30_000;

export const createPixelAccountSync = (
   api: PixelAccountApi,
   pixel: PixelAccountPixel = {
      setAllowed: setPixelAllowed,
      pause: pausePixel,
      identify: identifyPixelUser,
      clear: clearPixelUser,
      trackEvent,
   },
   now: () => number = Date.now,
) => {
   let current: PixelAccountContext | null = null;
   let generation = 0;
   let allowedNow = false;
   let lastPendingAt = 0;
   let pendingBusy = false;
   let decision = 0;

   const stop = () => {
      allowedNow = false;
      pixel.setAllowed(false);
      pixel.clear();
   };

   const refreshPending = async (force = false) => {
      const ctx = current;
      if (!ctx || !allowedNow || pendingBusy) return;
      if (!force && now() - lastPendingAt < PENDING_INTERVAL_MS) return;
      const mine = generation;
      pendingBusy = true;
      lastPendingAt = now();
      try {
         const pending = await api.fetchPending();
         if (mine !== generation || !allowedNow || !pending) return;
         // The API sends no user once this client may no longer be tracked, for example
         // after a withdrawal in another tab.
         if (!pending.user) {
            stop();
            return;
         }
         // The API's hashes stand in for an email the hub has not loaded yet.
         if (!ctx.email && pending.user?.em) await pixel.identify('', ctx.accountId, pending.user.em);
         if (mine !== generation || !allowedNow) return;
         const events = Array.isArray(pending.events) ? pending.events : [];
         for (const event of events) {
            // Each event is told to the API only once the library has taken it.
            pixel.trackEvent(
               event.name,
               { value: event.value, currency: event.currency },
               event.event_id,
               () => void api.markFired([event.event_id]).catch(() => undefined),
            );
         }
      } catch {
         // The next page load offers the same events again.
      } finally {
         pendingBusy = false;
      }
   };

   const apply = async (consent: AdConsent | null, mine: number) => {
      const ctx = current;
      if (!ctx || mine !== generation) return;
      const turn = ++decision;
      const stale = () => mine !== generation || turn !== decision;
      if (!consent || consent.tracking_allowed !== true || ctx.impersonating) {
         stop();
         return;
      }
      // The library keeps the email hash only from the first time it is told who this
      // is, so both hashes must be ready before it is allowed to start.
      let fallbackEmailHash = '';
      if (!ctx.email) {
         const pending = await api.fetchPending().catch(() => null);
         if (stale()) return;
         fallbackEmailHash = pending?.user?.em ?? '';
      }
      await pixel.identify(ctx.email, ctx.accountId, fallbackEmailHash);
      if (stale()) return;
      allowedNow = true;
      pixel.setAllowed(true);
      await refreshPending(true);
   };

   // The signed-in account changed (or appeared, or went away).
   const sync = async (ctx: PixelAccountContext) => {
      if (!ctx.accountKey) {
         if (current) {
            current = null;
            generation++;
            allowedNow = false;
            lastPendingAt = 0;
            pixel.clear();
            pixel.setAllowed(false);
         }
         return;
      }
      if (current?.accountKey === ctx.accountKey) {
         current = ctx;
         return;
      }
      const replacedAccount = !!current;
      current = ctx;
      const mine = ++generation;
      allowedNow = false;
      lastPendingAt = 0;
      pixel.clear();
      // An admin impersonating a client is never tracked, whatever the client chose.
      if (ctx.impersonating) {
         pixel.setAllowed(false);
         return;
      }
      pixel.pause(replacedAccount);
      let result: { consent: AdConsent | null; error: unknown } | null = null;
      try {
         result = await api.fetchConsent();
      } catch {
         result = null;
      }
      if (mine !== generation) return;
      // A failed read says nothing about the choice: the pixel stays paused for this page
      // load and the browser id is kept.
      if (!result || result.error) return;
      await apply(result.consent, mine);
   };

   // A save anywhere in the app: the popup or the switch in settings.
   const consentChanged = async (consent: AdConsent) => {
      if (!current || current.impersonating) return;
      await apply(consent, generation);
   };

   return { sync, consentChanged, refreshPending: () => refreshPending(false) };
};

export const usePixelAccountApi = (): PixelAccountApi => ({
   fetchConsent: () => useClientAccountSettingsStore().getAdConsent('client-ad-consent-pixel'),
   fetchPending: async () => {
      const { data, error } = await useIFetch<PixelPending>(
         apiRoutes.client.privacy.adConversionsPending,
         { method: 'GET', key: `client-pixel-pending-${Date.now()}` },
      );
      return error.value || !data.value ? null : data.value;
   },
   markFired: async (eventIds) => {
      await useIFetch(apiRoutes.client.privacy.adConversionsFired, {
         method: 'POST',
         body: { event_ids: eventIds },
         key: `client-pixel-fired-${eventIds.join('-')}`,
      });
   },
});
